/**
 * WHAT WAS SOLD, TO WHOM, AND WHAT MOVED.
 *
 * The owner's three questions, 9 October: "check how much gas was sold by type
 * who was best customer how many cylinders trnsacted that day". Until this
 * screen the system could answer none of them — `bills.revenue` reported by
 * charge type, which says how the money was earned and not what was sold.
 *
 * Two deliberate choices:
 *
 * QUANTITIES ARE SHOWN PER UNIT AND NEVER ADDED ACROSS THEM. Nm³, kg and
 * cylinders do not add up, and a single "quantity sold" column would be a
 * fiction of exactly the kind this release spent its day removing.
 *
 * THE DAY BOOK COUNTS CYLINDERS. Everywhere else in CTS the gas is the sale and
 * the shell is the container — but the shells are what somebody has to find in
 * the morning, so this one report is in drums on purpose.
 */
import * as React from 'react';
import { useState } from 'react';
import { api, type Session } from '../api.js';
import { useAsk, go } from '../hooks.js';
import { Banner, Card, Loading, Money, Register, Text, type Column } from '../ui.js';
import { Page } from '../shell.js';
import { count, date, money, qty, today, words } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('reports')!.messages;

/** 'YYYY-MM' of a day. */
const monthOf = (d: string) => d.slice(0, 7);

export function Reports({ session }: { session: Session }) {
  const [from, setFrom] = useState(monthOf(today()));
  const [to, setTo] = useState(monthOf(today()));
  const [day, setDay] = useState(today());

  const sold = useAsk(() => api.view('reports.gasSold', { from, to }), [from, to], MESSAGES);
  const best = useAsk(() => api.view('reports.bestCustomers', { from, to }), [from, to], MESSAGES);
  const book = useAsk(() => api.view('reports.dayBook', { on: day }), [day], MESSAGES);
  const fills = useAsk(() => api.view('reports.fills', { from, to }), [from, to], MESSAGES);

  const soldColumns: readonly Column<{ gas: string; unit: string; qty: number; netPaise: number; taxPaise: number; customers: number; charges: number }>[] = [
    { key: 'gas', head: 'Gas', cell: (r) => <strong>{words(r.gas)}</strong> },
    { key: 'qty', head: 'Sold', right: true, cell: (r) => qty(r.qty, r.unit) },
    { key: 'net', head: 'Value', right: true, cell: (r) => <Money paise={r.netPaise} /> },
    { key: 'tax', head: 'GST', right: true, cell: (r) => <Money paise={r.taxPaise} /> },
    { key: 'who', head: 'Customers', right: true, cell: (r) => count(r.customers) },
    { key: 'charges', head: 'Charges', right: true, cell: (r) => <span className="muted">{count(r.charges)}</span> },
  ];

  const bestColumns: readonly Column<{ customerId: number; customerName: string; netPaise: number; gasPaise: number; holdingPaise: number; otherPaise: number; cylindersTaken: number }>[] = [
    { key: 'who', head: 'Customer', cell: (r) => <strong>{r.customerName}</strong> },
    { key: 'net', head: 'Charged', right: true, cell: (r) => <strong><Money paise={r.netPaise} /></strong> },
    { key: 'gas', head: 'of which gas', right: true, cell: (r) => <Money paise={r.gasPaise} /> },
    { key: 'holding', head: 'rent', right: true, cell: (r) => <Money paise={r.holdingPaise} /> },
    { key: 'other', head: 'other', right: true, cell: (r) => <Money paise={r.otherPaise} /> },
    { key: 'cyls', head: 'Cylinders taken', right: true, cell: (r) => count(r.cylindersTaken) },
  ];

  return (
    <Page title="What was sold, and what moved"
          sub="the gas, the customers and the day — read from the charges the invoices were made from">

      <div className="toolbar" style={{ alignItems: 'flex-end' }}>
        {/* 11 Oct, the UI sweep: these asked for "2026-04" as free text, so a
            person had to know the format and could type a month that does not
            exist. The same month picker Billing uses. */}
        <Text name="from" label="From month" type="month" value={from} onChange={setFrom} />
        <Text name="to" label="To month" type="month" value={to} onChange={setTo} />
        <span className="muted">a quarter is three months — April to June</span>
      </div>

      <Card title="How much gas was sold, by type"
            sub="per gas and per unit: Nm³, kg and cylinders are counted apart because they do not add up">
        {sold.loading ? <Loading what="what was sold" />
          : sold.error ? <Banner kind="error" title={sold.error.title} {...(sold.error.detail ? { detail: sold.error.detail } : {})} />
          : (<>
              <Register columns={soldColumns} rows={sold.data!.rows}
                        empty="Nothing was sold in those months."
                        totals={<span>
                          {Object.entries(sold.data!.totalQtyByUnit).sort(([a], [b]) => a.localeCompare(b))
                            .map(([u, q]) => <b key={u} style={{ marginRight: 12 }}>{qty(q, u)}</b>)}
                          · <b><Money paise={sold.data!.netPaise} /></b> plus <Money paise={sold.data!.taxPaise} /> GST
                        </span>} />
            </>)}
      </Card>

      <Card title="Who bought the most"
            sub="by what they were charged, with the gas and the rent shown apart — an account that is mostly rent is a different kind of news">
        {best.loading ? <Loading what="the customers" />
          : best.error ? <Banner kind="error" title={best.error.title} {...(best.error.detail ? { detail: best.error.detail } : {})} />
          : <Register columns={bestColumns} rows={best.data!.rows}
                      empty="Nobody was charged anything in those months."
                      onOpen={(r) => { window.location.hash = `#/customers/${r.customerId}`; }}
                      totals={<span>Charged in all: <b><Money paise={best.data!.netPaise} /></b></span>} />}
      </Card>

      <Card title="Customers' own cylinders filled" sub="job work by customer: how many of their vessels, how many fills, how much gas — the toll-fill bill check">
        {fills.loading ? <Loading what="the fills" />
          : fills.error ? <Banner kind="error" title={fills.error.title} />
          : <Register columns={[
              { key: 'customer', head: 'Customer', cell: (r: { customerName: string }) => <strong>{r.customerName}</strong> },
              { key: 'cyl', head: 'Cylinders', right: true, cell: (r: { cylinders: number }) => count(r.cylinders) },
              { key: 'fills', head: 'Fills', right: true, cell: (r: { fills: number }) => count(r.fills) },
              { key: 'gas', head: 'Gas put in', right: true, cell: (r: { m3: number; kg: number }) => [r.m3 ? `${r.m3} Nm³` : '', r.kg ? `${r.kg} kg` : ''].filter(Boolean).join(' + ') || '—' },
            ]} rows={fills.data!.rows} onOpen={(r: { customerId: number }) => go(`#/customers/${r.customerId}?tab=cylinders`)} empty="No customer cylinder was filled in those months." />}
      </Card>

      <Card title="How many cylinders transacted" sub="counted on the day the movement happened, not the day it was typed">
        <div className="toolbar" style={{ alignItems: 'flex-end' }}>
          <Text name="day" label="Day" value={day} onChange={setDay} type="date" />
        </div>
        {book.loading ? <Loading what="the day" />
          : book.error ? <Banner kind="error" title={book.error.title} {...(book.error.detail ? { detail: book.error.detail } : {})} />
          : (<>
              <div className="kpis">
                <div className="kpi" style={{ ['--accent' as string]: 'var(--primary)' }}>
                  <div className="kpi-label">Went out</div><div className="kpi-value">{count(book.data!.out.cylinders)}</div>
                  <div className="kpi-note">{count(book.data!.out.challans)} load(s), {count(book.data!.out.customers)} customer(s)</div>
                </div>
                <div className="kpi" style={{ ['--accent' as string]: 'var(--good)' }}>
                  <div className="kpi-label">Came back</div><div className="kpi-value">{count(book.data!.back.cylinders)}</div>
                  <div className="kpi-note">{book.data!.back.faulty ? `${count(book.data!.back.faulty)} with something wrong` : 'all sound'}</div>
                </div>
                <div className="kpi" style={{ ['--accent' as string]: 'var(--money)' }}>
                  <div className="kpi-label">Filled — ours</div><div className="kpi-value">{count(book.data!.filled.cylinders)}</div>
                  <div className="kpi-note">{Object.entries(book.data!.filled.qtyByUnit).map(([u, q]) => qty(q, u)).join(' · ') || 'nothing filled'}</div>
                </div>
                <div className="kpi" style={{ ['--accent' as string]: 'var(--muted)' }}>
                  <div className="kpi-label">Filled — customers'</div><div className="kpi-value">{count(book.data!.jobFilled.cylinders)}</div>
                  <div className="kpi-note">{Object.entries(book.data!.jobFilled.qtyByUnit).map(([u, q]) => qty(q, u)).join(' · ') || 'no job work'}</div>
                </div>
              </div>
              <Register
                columns={[
                  { key: 'num', head: 'Document', cell: (r: { number: string | null; challanId: number }) => r.number ?? <span className="muted">draft #{r.challanId}</span> },
                  { key: 'kind', head: 'Kind', cell: (r: { kind: string }) => words(r.kind) },
                  { key: 'who', head: 'Customer', cell: (r: { customerName: string }) => r.customerName },
                  { key: 'cyls', head: 'Cylinders', right: true, cell: (r: { cylinders: number }) => count(r.cylinders) },
                  { key: 'status', head: 'Where it stands', cell: (r: { status: string }) => words(r.status) },
                ]}
                rows={book.data!.challans}
                empty={`Nothing moved on ${date(day)}.`}
                onOpen={(r) => { window.location.hash = `#/dispatch/${r.challanId}`; }} />
            </>)}
      </Card>
    </Page>
  );
}
